<template>
  <div class="role-manage">
    <!-- 统计卡片 -->
    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
          <i class="el-icon-user-solid"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ roleList.length }}</div>
          <div class="stat-label">角色总数</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon" style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
          <i class="el-icon-s-grid"></i>
        </div>
        <div class="stat-content">
          <div class="stat-value">{{ siteList.length }}</div>
          <div class="stat-label">授权微站数</div>
        </div>
      </div>
    </div>

    <!-- 操作栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索角色名称..."
          prefix-icon="el-icon-search"
          clearable
          class="search-input"
          @input="handleSearch"
        ></el-input>
      </div>
      <div class="toolbar-right">
        <el-button class="action-btn" @click="handleAdd">
          <i class="el-icon-plus"></i>
          <span>新增角色</span>
        </el-button>
      </div>
    </div>

    <!-- 角色列表 -->
    <el-table
      :data="filteredRoleList"
      v-loading="loading"
      class="modern-table"
      @row-click="handleRowClick"
    >
      <el-table-column prop="name" label="角色名称" min-width="160">
        <template v-slot="scope">
          <div class="role-name-cell">
            <div class="role-avatar">
              <i class="el-icon-user"></i>
            </div>
            <span class="role-name">{{ scope.row.name }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="description" label="描述" min-width="180" show-overflow-tooltip>
        <template v-slot="scope">
          <span class="desc-text">{{ scope.row.description || '暂无描述' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="授权微站" width="140" align="center">
        <template v-slot="scope">
          <el-tag
            v-if="scope.row.siteIds && scope.row.siteIds.length === 0"
            type="success"
            effect="plain"
            size="small"
          >不限</el-tag>
          <el-tag v-else type="primary" effect="plain" size="small">
            {{ scope.row.siteIds ? scope.row.siteIds.length : 0 }} 个微站
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="权限模块" min-width="220">
        <template v-slot="scope">
          <div class="perm-tags">
            <el-tag
              v-for="perm in scope.row.permissions"
              :key="perm.module"
              :type="getPermTagType(perm.module)"
              effect="plain"
              size="mini"
            >
              {{ getModuleLabel(perm.module) }} ({{ perm.actions.length }})
            </el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="创建时间" width="160" align="center">
        <template v-slot="scope">
          <span class="time-text">
            <i class="el-icon-time"></i>
            {{ scope.row.createdAt | formatDate('YYYY-MM-DD HH:mm') }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right" align="center">
        <template v-slot="scope">
          <div class="action-btns">
            <el-button type="primary" plain size="mini" @click.stop="handleEdit(scope.row)">
              <i class="el-icon-edit"></i> 编辑
            </el-button>
            <el-button type="danger" plain size="mini" @click.stop="handleDelete(scope.row)">
              <i class="el-icon-delete"></i> 删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      v-if="total > 20"
      class="modern-pagination"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="20"
      @current-change="handlePageChange"
    ></el-pagination>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="760px"
      top="5vh"
      class="role-dialog"
      :close-on-click-modal="false"
      @close="handleDialogClose"
    >
      <el-form ref="roleForm" :model="roleForm" :rules="rules" label-width="100px" label-position="right">
        <el-form-item label="角色名称" prop="name">
          <el-input
            v-model="roleForm.name"
            placeholder="请输入角色名称，如：内容管理员、运营人员"
            maxlength="50"
            prefix-icon="el-icon-user"
          ></el-input>
        </el-form-item>
        <el-form-item label="角色描述" prop="description">
          <el-input
            v-model="roleForm.description"
            type="textarea"
            :rows="2"
            placeholder="请输入角色描述，说明该角色的职责和权限范围"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>

        <!-- 权限矩阵 -->
        <el-form-item label="权限配置">
          <div class="permission-card">
            <div class="card-header">
              <span class="header-title">
                <i class="el-icon-s-check"></i> 功能权限矩阵
              </span>
              <el-checkbox v-model="allChecked" @change="handleToggleAll" class="check-all-btn">
                全选所有权限
              </el-checkbox>
            </div>
            <div class="matrix-header">
              <span class="module-col">功能模块</span>
              <span class="action-col">查看</span>
              <span class="action-col">新增</span>
              <span class="action-col">编辑</span>
              <span class="action-col">删除</span>
              <span class="action-col module-action">
                <el-checkbox :value="false" disabled> 行全选 </el-checkbox>
              </span>
            </div>
            <div
              v-for="mod in moduleList"
              :key="mod.value"
              class="matrix-row"
              :class="{ 'row-active': isModuleChecked(mod.value) }"
            >
              <span class="module-col">
                <i :class="getModuleIcon(mod.value)" class="module-icon"></i>
                {{ mod.label }}
              </span>
              <span class="action-col">
                <el-checkbox :value="isChecked(mod.value, 'view')" @input="toggle(mod.value, 'view')" />
              </span>
              <span class="action-col">
                <el-checkbox :value="isChecked(mod.value, 'create')" @input="toggle(mod.value, 'create')" />
              </span>
              <span class="action-col">
                <el-checkbox :value="isChecked(mod.value, 'edit')" @input="toggle(mod.value, 'edit')" />
              </span>
              <span class="action-col">
                <el-checkbox :value="isChecked(mod.value, 'delete')" @input="toggle(mod.value, 'delete')" />
              </span>
              <span class="action-col module-action">
                <el-checkbox
                  :value="isModuleChecked(mod.value)"
                  @input="handleToggleModule(mod.value)"
                > 全选 </el-checkbox>
              </span>
            </div>
          </div>
        </el-form-item>

        <!-- 授权微站 -->
        <el-form-item label="授权微站">
          <div class="site-auth-card">
            <el-select
              v-model="roleForm.siteIds"
              multiple
              filterable
              collapse-tags
              placeholder="留空表示不限制微站"
              style="width: 100%"
            >
              <el-option
                v-for="site in siteList"
                :key="site._id"
                :label="site.name"
                :value="site._id"
              ></el-option>
            </el-select>
            <div class="tip-text">
              <i class="el-icon-info"></i>
              留空表示该角色不限制微站访问范围，可访问所有微站
            </div>
          </div>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button class="cancel-btn" @click="dialogVisible = false">
          <i class="el-icon-close"></i> 取消
        </el-button>
        <el-button type="primary" class="confirm-btn" :loading="submitLoading" @click="handleSubmit">
          <i class="el-icon-check"></i> {{ editId ? '保存修改' : '确认创建' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getRoleList, addRole, editRole, deleteRole } from '@/api/role'
import request from '@/utils/request'

const MODULE_LIST = [
  { label: '站点配置', value: 'site' },
  { label: '微站管理', value: 'pages' },
  { label: '页面搭建', value: 'page-build' },
  { label: '直播管理', value: 'live' },
  { label: '素材管理', value: 'material' },
  { label: '用户管理', value: 'user' },
  { label: '嘉宾管理', value: 'guest' },
  { label: '日程管理', value: 'schedule' },
  { label: '短信任务', value: 'sms-task' },
]

const ACTIONS = ['view', 'create', 'edit', 'delete']

const MODULE_ICONS = {
  'site': 'el-icon-setting',
  'pages': 'el-icon-document',
  'page-build': 'el-icon-edit',
  'live': 'el-icon-video-camera',
  'material': 'el-icon-folder-opened',
  'user': 'el-icon-user',
  'guest': 'el-icon-s-custom',
  'schedule': 'el-icon-date',
  'sms-task': 'el-icon-message',
}

const PERM_TAG_TYPES = {
  'site': 'danger',
  'pages': 'primary',
  'page-build': 'success',
  'live': 'warning',
  'material': 'info',
  'user': '',
  'guest': 'success',
  'schedule': 'warning',
  'sms-task': 'info',
}

export default {
  name: 'role-manage',
  data() {
    return {
      roleList: [],
      loading: false,
      total: 0,
      page: 1,
      dialogVisible: false,
      dialogTitle: '新增角色',
      editId: '',
      submitLoading: false,
      siteList: [],
      searchKeyword: '',
      roleForm: {
        name: '',
        description: '',
        permissions: (() => { const p = {}; MODULE_LIST.forEach(m => p[m.value] = []); return p })(),
        siteIds: []
      },
      rules: {
        name: [{ required: true, message: '请输入角色名称', trigger: 'blur' }]
      },
      moduleList: MODULE_LIST
    }
  },
  computed: {
    allChecked() {
      return MODULE_LIST.every(mod =>
        this.roleForm.permissions[mod.value].length === ACTIONS.length
      )
    },
    filteredRoleList() {
      if (!this.searchKeyword) return this.roleList
      const kw = this.searchKeyword.toLowerCase()
      return this.roleList.filter(r =>
        r.name.toLowerCase().includes(kw) ||
        (r.description && r.description.toLowerCase().includes(kw))
      )
    }
  },
  created() {
    this.fetchList()
    this.fetchSiteList()
  },
  methods: {
    getModuleIcon(value) {
      return MODULE_ICONS[value] || 'el-icon-menu'
    },
    getPermTagType(value) {
      return PERM_TAG_TYPES[value] || ''
    },
    handleSearch() {
      // filtered by computed property
    },
    handleRowClick(row) {
      // Optional: could expand details
    },
    // 权限勾选相关
    isChecked(mod, action) {
      return (this.roleForm.permissions[mod] || []).includes(action)
    },
    toggle(mod, action) {
      const arr = this.roleForm.permissions[mod]
      const idx = arr.indexOf(action)
      if (idx >= 0) arr.splice(idx, 1)
      else arr.push(action)
    },
    isModuleChecked(mod) {
      return this.roleForm.permissions[mod].length === ACTIONS.length
    },
    handleToggleModule(mod) {
      if (this.isModuleChecked(mod)) {
        this.roleForm.permissions[mod] = []
      } else {
        this.roleForm.permissions[mod] = [...ACTIONS]
      }
    },
    handleToggleAll(val) {
      MODULE_LIST.forEach(mod => {
        this.roleForm.permissions[mod.value] = val ? [...ACTIONS] : []
      })
    },
    // 权限勾选相关 end

    async fetchList() {
      this.loading = true
      try {
        const res = await getRoleList({ page: this.page, pageSize: 20 })
        if (res.status === '10000') {
          this.roleList = res.list || []
          this.total = res.total || 0
        }
      } catch (e) {
        this.$message.error('获取角色列表失败')
      }
      this.loading = false
    },
    async fetchSiteList() {
      try {
        const res = await request({ url: '/site/list', method: 'GET' })
        if (res.status === '10000') {
          this.siteList = res.data || []
        }
      } catch (e) {
        // ignore
      }
    },
    getModuleLabel(value) {
      const mod = MODULE_LIST.find(m => m.value === value)
      return mod ? mod.label : value
    },
    handleAdd() {
      this.dialogTitle = '新增角色'
      this.editId = ''
      const permissions = {}
      MODULE_LIST.forEach(mod => { permissions[mod.value] = [] })
      this.roleForm = { name: '', description: '', permissions, siteIds: [] }
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.dialogTitle = '编辑角色'
      this.editId = row._id
      const permissions = {}
      MODULE_LIST.forEach(mod => {
        const perm = (row.permissions || []).find(p => p.module === mod.value)
        permissions[mod.value] = perm ? perm.actions : []
      })
      this.roleForm = {
        name: row.name,
        description: row.description || '',
        permissions,
        siteIds: row.siteIds || []
      }
      this.dialogVisible = true
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除该角色吗？删除后关联的子账号将失去对应权限。', '删除确认', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消'
        })
        const res = await deleteRole({ id: row._id })
        if (res.status === '10000') {
          this.$message.success('删除成功')
          this.fetchList()
        } else {
          this.$message.error(res.message || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') this.$message.error('删除失败')
      }
    },
    handleSubmit() {
      this.$refs.roleForm.validate(async (valid) => {
        if (!valid) return
        this.submitLoading = true
        // 转换权限格式
        const permissions = []
        Object.keys(this.roleForm.permissions).forEach(mod => {
          const actions = this.roleForm.permissions[mod]
          if (actions && actions.length > 0) {
            permissions.push({ module: mod, actions })
          }
        })
        const data = {
          name: this.roleForm.name,
          description: this.roleForm.description,
          permissions,
          siteIds: this.roleForm.siteIds
        }
        try {
          let res
          if (this.editId) {
            res = await editRole({ ...data, id: this.editId })
          } else {
            res = await addRole(data)
          }
          if (res.status === '10000') {
            this.$message.success(this.editId ? '编辑成功' : '创建成功')
            this.dialogVisible = false
            this.fetchList()
          } else {
            this.$message.error(res.message || '操作失败')
          }
        } catch (e) {
          this.$message.error('操作失败')
        }
        this.submitLoading = false
      })
    },
    handleDialogClose() {
      this.$refs.roleForm && this.$refs.roleForm.resetFields()
    },
    handlePageChange(page) {
      this.page = page
      this.fetchList()
    }
  }
}
</script>

<style lang="scss" scoped>
.role-manage {
  padding: 20px 0;

  // ==================== 统计卡片 ====================
  .stat-cards {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;

    .stat-card {
      display: flex;
      align-items: center;
      flex: 1;
      padding: 20px;
      background: #fff;
      border-radius: 12px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
      }

      .stat-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 56px;
        height: 56px;
        border-radius: 12px;
        margin-right: 16px;

        i {
          font-size: 28px;
          color: #fff;
        }
      }

      .stat-content {
        .stat-value {
          font-size: 28px;
          font-weight: 600;
          color: #303133;
          line-height: 1.2;
        }

        .stat-label {
          font-size: 13px;
          color: #909399;
          margin-top: 4px;
        }
      }
    }
  }

  // ==================== 工具栏 ====================
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 16px 20px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    .toolbar-left {
      .search-input {
        width: 280px;

        ::v-deep .el-input__inner {
          border-radius: 20px;
          padding-left: 36px;
        }
      }
    }

    .toolbar-right {
      .action-btn {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        border-radius: 20px;
        padding: 10px 20px;
        color: #fff;
        font-weight: 500;
        transition: all 0.3s ease;

        &:hover {
          opacity: 0.9;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        i {
          margin-right: 4px;
        }
      }
    }
  }

  // ==================== 表格 ====================
  ::v-deep .modern-table {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

    .el-table__header th {
      background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
      color: #606266;
      font-weight: 600;
      padding: 14px 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #f5f8ff !important;
      }
    }

    .el-table__body td {
      padding: 12px 0;
    }
  }

  .role-name-cell {
    display: flex;
    align-items: center;

    .role-avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      margin-right: 12px;

      i {
        color: #fff;
        font-size: 18px;
      }
    }

    .role-name {
      font-weight: 500;
      color: #303133;
    }
  }

  .desc-text {
    color: #909399;
    font-size: 13px;
  }

  .perm-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;

    .el-tag {
      border-radius: 4px;
      font-size: 12px;
    }
  }

  .time-text {
    color: #909399;
    font-size: 13px;

    i {
      margin-right: 4px;
      color: #c0c4cc;
    }
  }

  .action-btns {
    display: flex;
    gap: 8px;
    justify-content: center;

    .el-button {
      border-radius: 6px;
      padding: 6px 12px;
      font-size: 12px;

      &.el-button--primary {
        &:hover {
          background: #667eea;
          border-color: #667eea;
          color: #fff;
        }
      }

      &.el-button--danger {
        &:hover {
          background: #f56c6c;
          border-color: #f56c6c;
          color: #fff;
        }
      }
    }
  }

  // ==================== 分页 ====================
  .modern-pagination {
    margin-top: 20px;
    padding: 12px 20px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    text-align: right;

    ::v-deep .el-pagination {
      .btn-prev, .btn-next, .el-pager li {
        border-radius: 6px;
        min-width: 32px;
      }

      .el-pager li.active {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: #fff;
      }
    }
  }

  // ==================== 权限矩阵卡片 ====================
  .permission-card {
    border: 1px solid #e4e7ed;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
      border-bottom: 1px solid #e4e7ed;

      .header-title {
        font-weight: 600;
        font-size: 14px;
        color: #303133;

        i {
          margin-right: 6px;
          color: #667eea;
        }
      }

      .check-all-btn {
        ::v-deep .el-checkbox__label {
          font-weight: 500;
          color: #667eea;
        }

        ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
          background-color: #667eea;
          border-color: #667eea;
        }
      }
    }

    .matrix-header {
      display: flex;
      background: #fafbfc;
      padding: 10px 16px;
      font-weight: 600;
      font-size: 13px;
      color: #606266;
      border-bottom: 1px solid #ebeef5;

      .module-col {
        width: 140px;
        flex-shrink: 0;
      }

      .action-col {
        flex: 1;
        text-align: center;

        &.module-action {
          flex: 0 0 90px;
        }
      }
    }

    .matrix-row {
      display: flex;
      padding: 12px 16px;
      border-bottom: 1px solid #f0f0f0;
      align-items: center;
      transition: background 0.2s ease;

      &:last-child {
        border-bottom: none;
      }

      &.row-active {
        background: #f9f9ff;
      }

      &:hover {
        background: #f5f8ff;
      }

      .module-col {
        width: 140px;
        flex-shrink: 0;
        font-size: 13px;
        color: #303133;
        font-weight: 500;

        .module-icon {
          margin-right: 6px;
          color: #667eea;
        }
      }

      .action-col {
        flex: 1;
        text-align: center;

        &.module-action {
          flex: 0 0 90px;
        }

        ::v-deep .el-checkbox__label {
          font-size: 12px;
          color: #606266;
        }
      }
    }
  }

  // ==================== 微站授权卡片 ====================
  .site-auth-card {
    padding: 16px;
    background: #fafbfc;
    border-radius: 8px;
    border: 1px solid #e4e7ed;

    .tip-text {
      font-size: 12px;
      color: #909399;
      margin-top: 8px;
      line-height: 1.5;

      i {
        margin-right: 4px;
        color: #e6a23c;
      }
    }
  }

  // ==================== 权限矩阵隐藏checkbox样式 ====================
  ::v-deep .permission-card .matrix-row .action-col:nth-child(n+2):nth-child(-n+5) {
    .el-checkbox .el-checkbox__label {
      display: none;
    }

    .el-checkbox__inner {
      margin: 0 auto;
    }
  }
}

// ==================== 弹窗样式 ====================
::v-deep .role-dialog {
  border-radius: 16px;
  overflow: hidden;

  .el-dialog__header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px 24px;
    margin: 0;

    .el-dialog__title {
      color: #fff;
      font-size: 18px;
      font-weight: 600;
    }

    .el-dialog__headerbtn .el-dialog__close {
      color: #fff;
      font-size: 20px;

      &:hover {
        color: #f0f0f0;
      }
    }
  }

  .el-dialog__body {
    padding: 24px;
    max-height: 65vh;
    overflow-y: auto;
  }

  .el-form-item {
    margin-bottom: 20px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
    background: #fafbfc;
  }

  .dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .cancel-btn {
      border-radius: 8px;
      padding: 10px 24px;

      i {
        margin-right: 4px;
      }
    }

    .confirm-btn {
      border-radius: 8px;
      padding: 10px 28px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      font-weight: 500;

      i {
        margin-right: 4px;
      }

      &:hover {
        opacity: 0.9;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
      }
    }
  }
}
</style>
